import { appConfig } from 'shared';
import type { AccessibilityReview, LegalTexts, SharedDataType, Subprocessor } from '~/modules/auth/legal/legal-types';
import { lazyNamed } from '~/utils/lazy-named';

const { retention } = appConfig;

export type LegalSubject = keyof typeof legalConfig;

/** Sections are declared statically so the legal page needs no DOM scanning. */
export const legalConfig = {
  privacy: {
    component: lazyNamed(() => import('~/modules/auth/legal/privacy-text'), 'PrivacyText'),
    label: 'c:privacy_policy',
    sections: [
      { id: 'overview', label: null },
      { id: 'how-we-use-data', label: 'Data we process' },
      { id: 'data-processing', label: 'Who sees your data' },
      { id: 'subprocessors', label: 'Subprocessors' },
      { id: 'cookies', label: 'Cookies and storage' },
      { id: 'security', label: 'Security' },
      { id: 'your-rights', label: 'Your rights' },
      { id: 'changes', label: 'Policy changes' },
    ],
  },
  terms: {
    component: lazyNamed(() => import('~/modules/auth/legal/terms-text'), 'TermsText'),
    label: 'c:terms_of_use',
    sections: [
      { id: 'overview', label: null },
      { id: 'agreement', label: 'Agreement' },
      { id: 'accounts', label: 'Accounts' },
      { id: 'acceptable-use', label: 'Acceptable use' },
      { id: 'intellectual-property', label: 'Intellectual property' },
      { id: 'service-changes', label: 'Service changes' },
      { id: 'termination', label: 'Termination' },
      { id: 'disclaimers', label: 'Disclaimers' },
      { id: 'liability', label: 'Liability' },
      { id: 'indemnification', label: 'Indemnification' },
      { id: 'governing-law', label: 'Governing law' },
      { id: 'general', label: 'General' },
    ],
  },
  accessibility: {
    component: lazyNamed(() => import('~/modules/auth/legal/accessibility-text'), 'AccessibilityText'),
    label: 'c:accessibility',
    sections: [
      { id: 'overview', label: null },
      { id: 'what-you-can-do', label: 'What you can do' },
      { id: 'known-limitations', label: 'Known limitations' },
      { id: 'how-we-test', label: 'How we test' },
    ],
  },
} as const satisfies LegalTexts;

export const legalSubjects = Object.keys(legalConfig) as [LegalSubject, ...LegalSubject[]];

/** Default legal subject used when no or an unknown subject is requested. */
export const defaultLegalSubject = legalSubjects[0];

/** Subprocessors handling personal data; `optional: true` marks services an app can filter out. */
export const subprocessors: Subprocessor[] = [
  {
    slug: 'scaleway',
    name: 'Scaleway',
    legalName: 'Scaleway SAS',
    website: 'https://www.scaleway.com/',
    servicesProvided: ['Server', 'Database', 'Object storage'],
    processingActivities: ['Hosting and storing customer data', 'Serving and transferring files'],
    categoriesOfPersonalData: ['Contact data', 'Identifiers', 'Customer content and files'],
    purposes: ['Service hosting and data storage', 'Backup and availability', 'Content delivery'],
    country: 'France',
    dpa: { signed: true, effectiveDate: '2022-07-01', url: 'https://www.scaleway.com/en/terms-and-conditions/data-processing-agreement/' },
  },
  {
    slug: 'brevo',
    name: 'Brevo',
    legalName: 'Brevo',
    website: 'https://www.brevo.com/',
    servicesProvided: ['Transactional email delivery'],
    processingActivities: ['Sending verification, notification and system emails'],
    categoriesOfPersonalData: ['Email addresses', 'Names', 'Email content'],
    purposes: ['Transactional email delivery', 'Email deliverability tracking'],
    country: 'France',
    dpa: { signed: true, effectiveDate: '2023-01-01', url: 'https://www.brevo.com/legal/termsofuse/#data-processing-agreement' },
  },
  {
    slug: 'transloadit',
    name: 'Transloadit',
    legalName: 'Transloadit Ltd.',
    website: 'https://transloadit.com/',
    servicesProvided: ['File processing', 'Image and video encoding'],
    processingActivities: ['Transforming, validating and encoding uploaded files'],
    categoriesOfPersonalData: ['Uploaded files', 'File metadata'],
    purposes: ['File transformation and validation', 'Image processing'],
    country: 'Germany',
    dpa: { signed: true, effectiveDate: '2023-01-01', url: 'https://transloadit.com/legal/dpa/' },
    optional: true,
  },
  {
    slug: 'gleap',
    name: 'Gleap',
    legalName: 'Gleap GmbH',
    website: 'https://gleap.io/',
    servicesProvided: ['Customer support', 'Bug reporting'],
    processingActivities: ['Collecting and storing support messages, feedback and browser context'],
    categoriesOfPersonalData: ['Email addresses', 'Support messages', 'Browser and page context'],
    purposes: ['Customer support', 'Bug reporting and feedback'],
    country: 'Austria',
    dpa: { signed: true, effectiveDate: '2023-01-01', url: 'https://gleap.io/privacy-policy/' },
    optional: true,
  },
  {
    slug: 'maple',
    name: 'Maple',
    legalName: 'Makisuo, Inc.',
    website: 'https://maple.dev/',
    servicesProvided: ['Error and performance monitoring'],
    processingActivities: ['Collecting errors, traces and performance data'],
    categoriesOfPersonalData: ['IP addresses', 'User ids', 'Browser data', 'Pages visited'],
    purposes: ['Finding and fixing errors and slow pages'],
    country: 'United States',
    dpa: { signed: false, effectiveDate: '', url: '' },
    optional: true,
  },
  {
    slug: 'onedollarstats',
    name: 'One Dollar Stats',
    legalName: 'One Dollar Stats',
    website: 'https://onedollarstats.com/',
    servicesProvided: ['Page visit statistics'],
    processingActivities: ['Counting page visits without cookies'],
    categoriesOfPersonalData: ['Hashed IP address and browser', 'Pages visited'],
    purposes: ['Counting visits to our pages'],
    country: 'Ukraine (servers in Finland)',
    dpa: { signed: false, effectiveDate: '', url: '' },
  },
];

/** Shared data types describing personal data flows; `optional: true` marks filterable entries. */
export const sharedDataTypes: SharedDataType[] = [
  {
    slug: 'user_profile',
    name: 'Profile',
    purpose: 'Identify you, sign you in and remember your preferences.',
    legalBasis: 'Contract',
    dataCategories: ['name', 'email address', 'avatar', 'bio', 'passkeys (public key only)', 'authenticator app setup', 'preferences'],
    dataSubjects: ['end users'],
    storageLocation: 'Primary database (EU)',
    retentionPeriod: 'Until you delete your account: then gone from the Service at once and deleted within 90 days.',
  },
  {
    slug: 'auth_sessions',
    name: 'Sessions and devices',
    purpose: 'Keep you signed in, show your active sessions, warn you about sign-ins from a new device and limit abuse.',
    legalBasis: 'Contract and legitimate interest',
    dataCategories: ['hashed session token', 'device and browser', 'hashed IP address with country and network', 'hashed device id'],
    dataSubjects: ['end users'],
    storageLocation: 'Primary database (EU)',
    retentionPeriod: `Sessions last a week and are deleted ${retention.sessionDaysAfterExpiry} days after they end. Known devices: ${retention.deviceDaysUnseen} days after you last signed in from one, or until you delete your account.`,
  },
  {
    slug: 'organization_membership',
    name: 'Memberships',
    purpose: 'Manage roles and permissions within organizations.',
    legalBasis: 'Contract',
    dataCategories: ['organizations you belong to', 'role', 'join date', 'last seen'],
    dataSubjects: ['organization members'],
    storageLocation: 'Primary database (EU)',
    retentionPeriod: 'Until the membership or organization is removed.',
  },
  {
    slug: 'user_uploads',
    name: 'Files',
    purpose: 'Store and deliver files you upload.',
    legalBasis: 'Contract',
    dataCategories: ['files', 'file name, time and owner'],
    dataSubjects: ['end users', 'organization members'],
    storageLocation: 'Scaleway Object Storage (EU)',
    retentionPeriod: 'Until deleted, at the latest 90 days after account deletion.',
  },
  {
    slug: 'activity',
    name: 'Activity',
    purpose: 'Sync changes between devices, notify you and track what you have seen.',
    legalBasis: 'Contract',
    dataCategories: ['history of changes', 'notifications', 'seen items'],
    dataSubjects: ['end users'],
    storageLocation: 'Primary database (EU)',
    retentionPeriod: `${retention.activityDays} days.`,
  },
  {
    slug: 'transactional_emails',
    name: 'Emails',
    purpose: 'Send verification, sign-in and notification emails.',
    legalBasis: 'Contract',
    dataCategories: ['email address', 'email content', 'delivery status'],
    dataSubjects: ['end users'],
    storageLocation: 'Brevo (EU)',
    retentionPeriod: 'Delivery logs: 30 days.',
  },
  {
    slug: 'page_visits',
    name: 'Page visits',
    purpose: 'Count visits to our pages.',
    legalBasis: 'Legitimate interest',
    dataCategories: ['pages visited', 'referrer', 'country', 'hash of IP address and browser'],
    dataSubjects: ['visitors', 'end users'],
    storageLocation: 'One Dollar Stats (Finland)',
    retentionPeriod: 'The visitor hash changes daily.',
  },
  {
    slug: 'error_data',
    name: 'Monitoring',
    purpose: 'Find and fix errors and slow pages.',
    legalBasis: 'Legitimate interest',
    dataCategories: ['errors', 'performance data', 'pages visited', 'browser', 'IP address', 'user and session id in server logs'],
    dataSubjects: ['end users'],
    storageLocation: 'Maple (US)',
    retentionPeriod: '30 days by default.',
    optional: true,
  },
];

/** Accessibility review results the statement shows. Raak has not been reviewed yet, so nothing is claimed. */
export const accessibilityReview: AccessibilityReview = {
  standard: 'WCAG 2.2 Level AA',
  reviewedAt: null,
  limitations: [],
  report: null,
};
